/**
 * /dna — Music DNA.
 *
 * Paste a Spotify song link, get an analysed profile, pick one of three creative
 * directions, then generate from the resulting prompt. Layout and styles are verbatim
 * from the monolith's `view === "dna"` block; the two sections below the URL field live
 * in components/dna/.
 *
 * SIMULATED. Nothing is fetched from Spotify and no audio is analysed — the profile is
 * hashed from the URL string, so the same link always yields the same result. The one
 * part that is real is that Spotify links are detected and carried into the request. See
 * src/stores/dnaStore.ts and src/lib/music.ts.
 *
 * All three actions wait behind the same processing state the composer and the Genre
 * Mixer show (components/studio/ProcessingWave), which is what makes the three creation
 * paths feel like one product. WAITING below is careful about which of the three waits
 * is real; the stores are the place that decides how long each one lasts.
 */
import { Dna, Link as LinkIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Btn, SectionHeading } from '../components/ui';
import { DnaAnalysisDashboard, DnaPromptComposer } from '../components/dna';
import { ProcessingWave } from '../components/studio';
import { TEXT_MUSIC_GENERATION_CREDITS } from '../lib/credits';
import { mapDnaGenreToAppGenre } from '../lib/music';
import { useSongs } from '../hooks';
import { useCreateStore } from '../stores/createStore';
import { useDnaStore } from '../stores/dnaStore';
import { useUiStore } from '../stores/uiStore';
import { c } from '../styles/theme';
import { paths } from './paths';

/**
 * The three processing states' words.
 *
 * Together, so they can be read against each other. Only two of the three describe a
 * wait for something: `analyzing` is held over a real (mock) request, and `generating`
 * over a real generation. `regenerating` waits for nothing — the reroll is a string
 * builder over data already on the screen — so its words claim only that the prompt is
 * being rewritten, which is true, and not that anything is being analysed again.
 */
const WAITING = {
  analyzing: {
    title: 'Analyzing the musical DNA',
    detail: 'Reading genre, mood, energy, tempo and key from the track.',
  },
  regenerating: {
    title: 'Rewriting your prompt',
    detail: 'Taking a different angle on the same musical DNA.',
  },
  generating: {
    title: 'Creating your song',
    detail: 'This usually takes about a minute or two. Please hold..',
  },
} as const;

export default function MusicDNA() {
  const navigate = useNavigate();

  /** The accepted service. */
  const sourceLabel = 'Spotify';

  // One field per selector — Zustand v5 compares with Object.is, so an object
  // literal would re-render this route on every unrelated store change.
  const url = useDnaStore((s) => s.url);
  const analyzing = useDnaStore((s) => s.analyzing);
  const regenerating = useDnaStore((s) => s.regenerating);
  const error = useDnaStore((s) => s.error);
  const dna = useDnaStore((s) => s.dna);
  const directions = useDnaStore((s) => s.directions);
  const selectedDirection = useDnaStore((s) => s.selectedDirection);
  const prompt = useDnaStore((s) => s.prompt);
  const inspirationStrength = useDnaStore((s) => s.inspirationStrength);
  // Only used to vary the reroll wave's bars — see the ProcessingWave below.
  const variant = useDnaStore((s) => s.variant);
  const setUrl = useDnaStore((s) => s.setUrl);
  const setPrompt = useDnaStore((s) => s.setPrompt);
  const setInspirationStrength = useDnaStore((s) => s.setInspirationStrength);
  const analyze = useDnaStore((s) => s.analyze);
  const selectDirection = useDnaStore((s) => s.selectDirection);
  const regeneratePrompt = useDnaStore((s) => s.regeneratePrompt);

  const loadIntoCreate = useCreateStore((s) => s.load);
  const setSurfaceResultSongId = useCreateStore((s) => s.setSurfaceResultSongId);
  const { generating, createSong } = useSongs();
  const showToast = useUiStore((s) => s.showToast);

  async function handleAnalyze() {
    if (await analyze()) showToast('Music DNA analyzed');
  }

  async function handleRegenerate() {
    // Awaited now, so the toast lands with the new text rather than a whole processing
    // floor before it. The store holds the wait; see dnaStore.regeneratePrompt.
    await regeneratePrompt();
    showToast('Prompt regenerated');
  }

  /**
   * Generates straight from the DNA prompt, seeding the Create form on the way so
   * the user lands on a screen that shows what was generated.
   */
  async function generateFromDna() {
    if (!prompt.trim()) {
      showToast('Analyze a song and generate a prompt first');
      return;
    }
    const mappedGenre = mapDnaGenreToAppGenre(dna?.genre);
    loadIntoCreate({ prompt, genre: mappedGenre });
    const song = await createSong(
      prompt,
      mappedGenre,
      TEXT_MUSIC_GENERATION_CREDITS,
      undefined,
      undefined,
      // Names the path, because it is one of the two Lyrixa Composer+ sells: the funnel
      // refuses it without the plan, before charging anything. See
      // hooks/useSongs.METHOD_ENTITLEMENT, and the gate in router.tsx that means an
      // unentitled visitor is shown the upgrade prompt instead of this screen.
      { method: 'dna' }
    );
    // Only navigates when generation actually started — a short balance leaves the user
    // here with the out-of-credits modal, and a missing Composer+ plan with the toast.
    if (song) {
      setSurfaceResultSongId('create', song.id);
      navigate(paths.create);
    }
  }

  return (
    <div>
      <SectionHeading
        as="h1"
        icon={<Dna size={17} color={c.accent} />}
        title="Music DNA"
        subtitle={`Paste a ${sourceLabel} song link — Lyrixsa AI analyzes its musical DNA and turns it into a brand-new, original prompt`}
      />

      <div
        style={{
          background: c.surface1,
          border: `1px solid ${c.border}`,
          borderRadius: 12,
          padding: '14px 16px',
          marginBottom: 20,
        }}
      >
        <label
          htmlFor="dna-url"
          style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}
        >
          {sourceLabel} song link
        </label>
        {/* A real <form>, so Enter in the link field starts the analysis — the same fix
            Login.tsx, NewProjectForm.tsx, Ticket.tsx, Playlists.tsx and LicenseVerify.tsx
            carry, with the same note: "a real <form> so Enter submits, which the
            prototype's div did not". A pasted URL followed by Enter is the whole
            interaction this screen is for. preventDefault because there is nowhere to post
            to; `handleAnalyze` is the submission and it already refuses an empty or
            unsupported link. */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleAnalyze();
          }}
          style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
        >
          <div style={{ flex: '1 1 200px', position: 'relative' }}>
            <LinkIcon
              size={14}
              color={c.text3}
              style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}
              aria-hidden="true"
            />
            <input
              id="dna-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://open.spotify.com/track/..."
              style={{
                width: '100%',
                background: c.surface2,
                border: `1px solid ${c.border}`,
                borderRadius: 8,
                fontSize: 13,
                padding: '9px 10px 9px 32px',
                color: c.text1,
                boxSizing: 'border-box',
              }}
            />
          </div>
          <Btn type="submit" disabled={analyzing} style={{ flexShrink: 0 }}>
            <Dna size={14} />
            {analyzing ? 'Analyzing…' : 'Analyze'}
          </Btn>
        </form>
        {/* role="alert" so the refusal is announced. It appears in response to a press,
            after the field it is about has already been read, so a screen reader user got
            no indication that anything had happened — the same reason TrimPanel's field
            error and the load failures across the app are live regions. */}
        {error && (
          <p role="alert" style={{ fontSize: 11, color: c.danger, margin: '8px 0 0' }}>
            {error}
          </p>
        )}
      </div>

      {/* The analysis wave stands in for the empty state and the dashboard both, because
          what it is preparing is the thing that would otherwise be in this position. */}
      {analyzing && <ProcessingWave size="lg" seed={`Lyrixa-dna-${url}`} {...WAITING.analyzing} />}

      {!dna && !analyzing && (
        <div
          style={{
            border: `1px dashed ${c.border}`,
            borderRadius: 12,
            padding: '24px 20px',
            textAlign: 'center',
          }}
        >
          <Dna size={22} color={c.text3} style={{ marginBottom: 8 }} />
          <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>
            Paste a link above to see genre, mood, energy, BPM, and more — then turn it into an
            original prompt.
          </p>
        </div>
      )}

      {dna && !analyzing && (
        <>
          {/* The dashboard stays up through a reroll: the profile is not what changes.
              What is being rewritten is the prompt, so only the composer below is
              replaced — the wave sits exactly where the text it is producing will be. */}
          <DnaAnalysisDashboard dna={dna} />
          {regenerating ? (
            <ProcessingWave
              size="lg"
              // Varies with the reroll counter, so a second reroll does not redraw the
              // identical silhouette. Cosmetic; the bars are a hash, not a signal.
              seed={`Lyrixa-dna-reroll-${variant}`}
              {...WAITING.regenerating}
            />
          ) : (
            <DnaPromptComposer
              directions={directions}
              selectedDirection={selectedDirection}
              onSelectDirection={selectDirection}
              prompt={prompt}
              onPromptChange={setPrompt}
              inspirationStrength={inspirationStrength}
              onStrengthChange={setInspirationStrength}
              onRegenerate={() => void handleRegenerate()}
              onGenerate={() => void generateFromDna()}
              regenerating={regenerating}
              generating={generating}
            />
          )}

          {/* Generation goes below the composer rather than replacing it, matching
              Create: the prompt that produced the song stays readable while it runs. */}
          {generating && (
            <ProcessingWave
              size="lg"
              seed={`Lyrixa-dna-generate-${dna.genre}`}
              style={{ marginTop: 18 }}
              {...WAITING.generating}
            />
          )}
        </>
      )}
    </div>
  );
}
